/**
 * @fileoverview Reusable confirmation modal for deleting a selected set of records.
 * @module ui-admin/components/ui/ConfirmBulkDeleteModal
 *
 * Responsibilities:
 * - Summarize records and optional warnings before a bulk delete.
 * - Invoke the host confirmation callback and prevent duplicate submission.
 * Dependencies: React, lucide-react.
 * Architecture: Reusable presentation component; records and mutation callbacks come from the host.
 * Notes: Escape and backdrop interactions close the modal; submission state disables confirmation.
 */
import { useEffect } from 'react';
import { AlertTriangle, X, Trash2 } from 'lucide-react';

/**
 * @contract BulkDeleteRecord
 * Purpose: Describes one record displayed in a bulk-delete confirmation.
 * Fields: id, label, sublabel (optional).
 * Rules: id must uniquely identify the record within the supplied list.
 * Sync: Display-only data; the host owns record selection and deletion.
 */
export interface BulkDeleteRecord {
  /** Unique identifier for the record */
  id: string;
  /** Display label shown in the record list (e.g. contact name, entity name) */
  label: string;
  /** Optional secondary info shown after the label (e.g. role, status) */
  sublabel?: string;
}

/**
 * @contract ConfirmBulkDeleteModalProps
 * Purpose: Configures the records and host-owned action for a bulk-delete prompt.
 * Fields: title, description, records, warning, deleteButtonLabel, isSubmitting, onConfirm, onClose.
 * Rules: onConfirm may be asynchronous; repeated submission is blocked while isSubmitting is true.
 * Sync: The host owns deletion and submission-state updates.
 */
export interface ConfirmBulkDeleteModalProps {
  /** Modal title, e.g. "Delete 5 Contacts?" */
  title: string;
  /** Description paragraph(s) shown below the title */
  description: React.ReactNode;
  /** The records being deleted — rendered as a scrollable list */
  records: BulkDeleteRecord[];
  /** Optional warning block (e.g., cascade effects, linked entities) */
  warning?: React.ReactNode;
  /** Text for the delete button, default "Yes, Delete All" */
  deleteButtonLabel?: string;
  /** Whether the delete operation is in progress */
  isSubmitting?: boolean;
  /** Called when user confirms */
  onConfirm: () => void | Promise<void>;
  /** Called when user cancels or clicks backdrop */
  onClose: () => void;
}

/**
 * @component ConfirmBulkDeleteModal
 * Purpose: Confirm deletion of one or more records with a visible record summary.
 * Props: Title, description, record list, optional warning/button label/submission state, and callbacks.
 * Behavior: Renders record labels and warnings, then awaits confirmation or closes on cancel.
 * Data Source: local (record details and callbacks supplied by the host).
 * Constraints: The host owns deletion, record selection, and modal lifecycle.
 * Performance: Renders only the supplied records and attaches one Escape-key listener while mounted.
 */
export default function ConfirmBulkDeleteModal({
  title,
  description,
  records,
  warning,
  deleteButtonLabel,
  isSubmitting = false,
  onConfirm,
  onClose,
}: ConfirmBulkDeleteModalProps) {
  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    await onConfirm();
  };

  const label = deleteButtonLabel ?? `Yes, Delete ${records.length === 1 ? 'Record' : `All ${records.length}`}`;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center gap-2 bg-red-50">
          <AlertTriangle className="text-red-600 h-5 w-5 shrink-0" />
          <h3 className="font-semibold text-red-900">{title}</h3>
          <button onClick={onClose} className="ml-auto text-red-400 hover:text-red-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Description */}
          <p className="text-sm text-gray-600">{description}</p>

          {/* Record list */}
          {records.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 bg-gray-100">
                <p className="text-xs font-medium text-gray-500">
                  {records.length} {records.length === 1 ? 'record' : 'records'} will be deleted:
                </p>
              </div>
              <ul className="max-h-48 overflow-auto divide-y divide-gray-100">
                {records.map(rec => (
                  <li key={rec.id} className="px-3 py-2 flex items-center gap-2 text-sm">
                    <Trash2 className="h-3 w-3 text-red-400 shrink-0" />
                    <span className="font-medium text-gray-800 truncate">{rec.label}</span>
                    {rec.sublabel && (
                      <span className="text-xs text-gray-400 truncate ml-auto">{rec.sublabel}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Optional warning */}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>{warning}</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors"
            >
              No, Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Deleting...' : label}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
